import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import { fixTimezoneOffsetYYYYMMDD } from '../../constants/constants';
import URLConstants from '../../constants/url-constants';
import config from '../../helpers/config-helper';
import { IAddTray, IUpdateTray } from '../../models/forms/IAddUpdateTray';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  ITrayFile,
  ITrayFileListVM,
  ITrayList,
  ITrayListVM,
  ITrayVM,
} from '../../models/response/ITrayResponse';
import { ITrayManagementState } from '../../models/state/ITrayManagementState';
import { formatMessage } from '../../translations/format-message';
import {
  GetAllTrayFileState,
  GetAllTrayState,
  GetTrayState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
const appConfig = config();

export class TrayManagementStore implements ITrayManagementState {
  initialStateValue = ObservableInitialState;
  trayList: ITrayListVM = GetAllTrayState;
  tray: ITrayVM | undefined = undefined;
  trayState = { ...this.initialStateValue };
  addUpdateTrayState = { ...this.initialStateValue };
  deleteTrayState = { ...this.initialStateValue };
  getTrayListServiceState = { ...this.initialStateValue };
  fileUploadState = { ...this.initialStateValue };
  duplicateData: string = null;
  getTrayFilesHistoryState = { ...this.initialStateValue };
  trayFiles: ITrayFileListVM = GetAllTrayFileState;
  getDownloadrayFilesHistoryState = { ...this.initialStateValue };
  getTrayReportState = {...this.initialStateValue};
  base64String: any = { fileString: '' };
  private trayListRequestId = 0;

  constructor() {
    makeObservable(this, {
      tray: observable,
      trayState: observable,
      trayList: observable,
      addUpdateTrayState: observable,
      deleteTrayState: observable,
      getTrayListServiceState: observable,
      GetTrayListService: action,
      GetTrayByIdService: action,
      AddTrayService: action,
      UpdateTrayService: action,
      DeleteTrayService: action,
      resetDeleteTray: action,
      resetAddUpdateTrayState: action,
      reset: action,
      allTrays: computed,
      trayDetails: computed,
      trayFileUploadService: action,
      fileUploadState: observable,
      resetFileUploadState: action,
      allTrayIds: computed,
      GetTrayFilesHistoryService: action,
      getTrayFilesHistoryState: observable,
      trayFilesHistory: computed,
      DownloadTrayFilesHistoryService: action,
      getDownloadrayFilesHistoryState: observable,
      trayFiles: observable,
      resetExportFileState: action,
      GetTrayReportService: action,
      getTrayReportState: observable,
      resetGetTrayReportState: action,
    });
  }

  //#region Actions
  GetTrayListService = (
    TenantId: number,
    PageSize: number,
    PageNo: number,
    filters: string
  ) => {
    const requestId = ++this.trayListRequestId;
    this.getTrayListServiceState.inProgress = true;
    const url = `${URLConstants.GetAllTrayList}?TenantId=${TenantId}&PageSize=${PageSize}&PageNo=${PageNo}&filters=${filters}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ITrayListVM>>) => {
        if (requestId !== this.trayListRequestId) {
          return;
        }
        if (response.data.Error) {
          this.getTrayListServiceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.trayList = response.data.Data;
          this.getTrayListServiceState.success = true;
        }
      })
      .catch((err: string) => {
        if (requestId !== this.trayListRequestId) {
          return;
        }
        this.getTrayListServiceState.error = err;
      })
      .finally(
        action(() => {
          if (requestId === this.trayListRequestId) {
            this.getTrayListServiceState.inProgress = false;
          }
        })
      );
  };

  AddTrayService = (data: IAddTray) => {
    this.addUpdateTrayState.inProgress = true;
    const url = URLConstants.AddTray;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateTrayState.error = response.data.Message;
        } else {
          this.addUpdateTrayState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateTrayState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateTrayState.inProgress = false;
        })
      );
  };

  UpdateTrayService = (data: IUpdateTray) => {
    this.addUpdateTrayState.inProgress = true;
    let url = URLConstants.UpdateTray;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateTrayState.error = response.data.Message;
        } else {
          this.addUpdateTrayState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateTrayState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateTrayState.inProgress = false;
        })
      );
  };

  GetTrayByIdService = (TenantId: number, TrayId: number) => {
    this.trayState.inProgress = true;
    let url = `${URLConstants.GetTrayById}?TenantId=${TenantId}&TrayId=${TrayId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ITrayVM>>) => {
        if (response.data.Error) {
          this.trayState.error = response.data.Message;
        } else {
          this.tray = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.trayState.error = err;
      })
      .finally(
        action(() => {
          this.trayState.inProgress = false;
        })
      );
  };

  DeleteTrayService = (TenantId: number, TrayId: number) => {
    this.deleteTrayState.inProgress = true;
    let url = `${URLConstants.DeleteTray}?TenantId=${TenantId}&TrayId=${TrayId}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteTrayState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteTrayState.success = true;
      })
      .catch((err: string) => {
        this.deleteTrayState.error = err;
      })
      .finally(
        action(() => {
          this.deleteTrayState.inProgress = false;
        })
      );
  };

  trayFileUploadService = (TenantId: number, csvFile: File) => {
    this.fileUploadState.inProgress = true;
    const url = `${URLConstants.UploadTrayFile}?TenantId=${TenantId}`;
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    return baseService
      .postMultipart(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.fileUploadState.error = response.data.Message;
        } else {
          if (response.data.Data) {
            this.duplicateData = response.data.Data;
          }
          this.fileUploadState.success = true;
        }
      })
      .catch((err: string) => {
        this.fileUploadState.error = formatMessage('SomethingWentWrong');
        console.error(err);
      })
      .finally(
        action(() => {
          this.fileUploadState.inProgress = false;
        })
      );
  };

  GetTrayFilesHistoryService = (
    tenantId: number,
    PageNo: number,
    PageSize: number,
    startDate: string,
    endDate: string
    // filters: string
  ) => {
    this.getTrayFilesHistoryState.inProgress = true;
    const url = `${URLConstants.GetTrayFilesHistory}?TenantId=${tenantId}&PageSize=${[PageSize]}&PageNo=${PageNo}&startDate=${startDate}&endDate=${endDate}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ITrayFileListVM>>) => {
        if (response.data.Error) {
          this.getTrayFilesHistoryState.error = response.data.Message;
        } else {
          this.trayFiles = response.data.Data;
          this.getTrayFilesHistoryState.success = true;
        }
      })
      .catch((err: string) => {
        this.getTrayFilesHistoryState.error = err;
      })
      .finally(
        action(() => {
          this.getTrayFilesHistoryState.inProgress = false;
        })
      );
  };

  DownloadTrayFilesHistoryService = (tenantId: number, fileId: number) => {
    this.getDownloadrayFilesHistoryState.inProgress = true;
    const url = `${URLConstants.DownloadTrayFileHistory}?TenantId=${tenantId}&fileID=${fileId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ITrayFileListVM>>) => {
        if (response.data.Error) {
          this.getDownloadrayFilesHistoryState.error = response.data.Message;
        } else {
          this.getDownloadrayFilesHistoryState.success = true;
          this.base64String = { fileString: response.data.Data };
        }
      })
      .catch((err: string) => {
        this.getDownloadrayFilesHistoryState.error = err;
      })
      .finally(
        action(() => {
          this.getDownloadrayFilesHistoryState.inProgress = false;
        })
      );
  };

  GetTrayReportService = (tenantId: number, trayId: number, trayName: string) => {
    this.getTrayReportState.inProgress = true;
    const url = `${URLConstants.GetTrayReport}?TenantId=${tenantId}&TrayId=${trayId}&TrayName=${trayName}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ITrayFileListVM>>) => {
        if (response.data.Error) {
          this.getTrayReportState.error = response.data.Message;
        } else {
          this.getTrayReportState.success = true;
          this.base64String = { fileString: response.data.Data };
        }
      })
      .catch((err: string) => {
        this.getTrayReportState.error = err;
      })
      .finally(
        action(() => {
          this.getTrayReportState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  get allTrays(): ITrayList[] {
    let trayData: ITrayList[] = [];
    if (this.trayList?.Trays && this.trayList.Trays?.length > 0) {
      this.trayList.Trays.map(
        ({
          TrayId,
          CustomerId,
          Id,
          EndUserName,
          EndUserOrderId,
          OrderId,
          SubContractor,
          SubContractorName,
          ProductId,
          Katashiki1,
          Katashiki2,
          Kishu,
          ProcessName,
          Quantity,
          InvoiceNumber,
          QuantityUnitName,
          CustomerDueDate,
          MissingParts,
          PlanedDueDate,
          Group,
          ProductComment1,
          ProductComment2,
          ProductComment3,
          ProductComment4,
          ProductComment5,
          ProductComment6,
          CurrentState,
          Remarks,
          isSelected,
          ProductName,
          Code,
          TagId
        }) => {
          trayData.push({
            TrayId,
            CustomerId: CustomerId || '',
            Id,
            EndUserName,
            EndUserOrderId,
            OrderId,
            SubContractor: SubContractor || '',
            SubContractorName,
            ProductId,
            Katashiki1,
            Katashiki2,
            Kishu,
            ProcessName,
            Quantity: Quantity || '',
            InvoiceNumber: InvoiceNumber || '',
            QuantityUnitName,
            MissingParts: MissingParts === '-1' ? 'N/A' : MissingParts,
            ProductComment1,
            ProductComment2,
            ProductComment3,
            ProductComment4,
            ProductComment5,
            ProductComment6,
            Remarks,
            isSelected,
            ProductName,
            Group,
            CurrentState,
            CustomerDueDate: fixTimezoneOffsetYYYYMMDD(CustomerDueDate),
            PlanedDueDate: fixTimezoneOffsetYYYYMMDD(PlanedDueDate),
            // CustomerDueDate: convertNonUTCDateToLocalTimeZone(
            //   fixTimezoneOffset(CustomerDueDate)
            // ),
            // PlanedDueDate: convertNonUTCDateToLocalTimeZone(
            //   fixTimezoneOffset(PlanedDueDate)
            // ),
            Code,
            TagId,
          });
        }
      );
    }
    return trayData;
  }

  get trayDetails(): ITrayVM {
    if (this.tray) {
      const {
        TrayId,
        CustomerId,
        Id,
        EndUserName,
        EndUserOrderId,
        OrderId,
        SubContractor,
        SubContractorName,
        ProductId,
        Katashiki1,
        Katashiki2,
        Kishu,
        ProcessName,
        Quantity,
        InvoiceNumber,
        QuantityUnitName,
        CustomerDueDate,
        MissingParts,
        PlanedDueDate,
        Group,
        ProductComment1,
        ProductComment2,
        ProductComment3,
        ProductComment4,
        ProductComment5,
        ProductComment6,
        CurrentState,
        Remarks,
        isSelected,
        ProductName,
        Code,
        TagId
      } = this.tray;

      return {
        TrayId,
        CustomerId: CustomerId || '',
        Id,
        EndUserName,
        EndUserOrderId,
        OrderId,
        SubContractorName,
        Kishu,
        ProcessName,
        Quantity: Quantity || '',
        InvoiceNumber: InvoiceNumber || '',
        QuantityUnitName,
        MissingParts: MissingParts === '有' ? MissingParts : '無',
        Group,
        ProductComment1,
        ProductComment2,
        ProductComment3,
        ProductComment4,
        ProductComment5,
        ProductComment6,
        CurrentState,
        Remarks,
        isSelected,
        ProductName,
        CustomerDueDate: fixTimezoneOffsetYYYYMMDD(CustomerDueDate),
        PlanedDueDate: fixTimezoneOffsetYYYYMMDD(PlanedDueDate),
        // PlanedDueDate: convertNonUTCDateToLocalTimeZone(
        //   fixTimezoneOffset(PlanedDueDate)
        // ),
        Katashiki1,
        Katashiki2,
        SubContractor: SubContractor || '',
        Code,
        ProductId:String(ProductId),
        TagId
      };
    }
    return {
      Id: -1,
      ProductName: '',
      Code: '',
      TagId: '',
      ...GetTrayState,
    };
  }

  get allTrayIds(): number[] {
    let trayData: number[] = [];
    if (this.trayList?.Trays && this.trayList.Trays?.length > 0) {
      this.trayList.Trays.map(({ Id }) => {
        trayData.push(Id);
      });
    }
    return trayData;
  }

  get trayFilesHistory(): ITrayFile[] {
    const files = [];
    if (this.trayFiles) {
      this.trayFiles.ImportStatusList.forEach((file: ITrayFile) =>
        files.push({
          ...file,
          ImportTime: fixTimezoneOffsetYYYYMMDD(file.ImportTime),
        })
      );
      return files;
    }
    return [];
  }

  //#endregion

  //#region Reset
  reset = () => {
    this.trayList = GetAllTrayState;
  };

  resetTrayDetail = () => {
    this.tray = undefined;
    this.trayState = { ...this.initialStateValue };
    this.duplicateData = '';
  };

  resetDeleteTray = () => {
    this.deleteTrayState = { ...this.initialStateValue };
  };

  resetAddUpdateTrayState = () => {
    this.addUpdateTrayState.inProgress = false;
    this.addUpdateTrayState.success = false;
    this.addUpdateTrayState.error = '';
  };

  resetFileUploadState = () => {
    this.fileUploadState.inProgress = false;
    this.fileUploadState.success = false;
    this.fileUploadState.error = '';
  };

  resetExportFileState = () => {
    this.getDownloadrayFilesHistoryState = { ...this.initialStateValue };
  };

  resetGetTrayReportState = () => {
    this.getTrayReportState = {...this.initialStateValue};
  }
  //#endregion
}

export default new TrayManagementStore();
